import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Check, Sparkles, ArrowRight, X } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

// Discount tier: total boxes in bundle -> percent off
const TIERS = [
  { min: 1, off: 0, label: "Single item" },
  { min: 2, off: 10, label: "Duo · save 10%" },
  { min: 3, off: 15, label: "Trio · save 15%" },
  { min: 4, off: 18, label: "Quartet · save 18%" },
  { min: 5, off: 20, label: "Atelier Five · save 20%" },
];

const getTier = (count) => {
  let active = TIERS[0];
  for (const t of TIERS) if (count >= t.min) active = t;
  return active;
};

export default function BundleBuilder() {
  const [products, setProducts] = useState([]);
  const [selection, setSelection] = useState({}); // {productId: qty}
  const { add, setIsOpen } = useCart();

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  // One card per signature category — pick first product per category
  const signatures = useMemo(() => {
    const seen = new Set();
    return products.filter((p) => {
      if (seen.has(p.category)) return false;
      seen.add(p.category);
      return true;
    });
  }, [products]);

  const totalItems = useMemo(
    () => Object.values(selection).reduce((s, q) => s + q, 0),
    [selection]
  );
  const subtotal = useMemo(
    () =>
      Object.entries(selection).reduce((s, [id, q]) => {
        const p = products.find((x) => x.id === id);
        return s + (p ? p.price * q : 0);
      }, 0),
    [selection, products]
  );

  const tier = getTier(totalItems);
  const discountAmt = Math.round((subtotal * tier.off) / 100);
  const total = subtotal - discountAmt;
  const nextTier = TIERS.find((t) => t.min > totalItems);

  const update = (id, delta) => {
    setSelection((prev) => {
      const cur = prev[id] || 0;
      const next = Math.max(0, cur + delta);
      const out = { ...prev };
      if (next === 0) delete out[id];
      else out[id] = next;
      return out;
    });
  };

  const reset = () => setSelection({});

  const addBundleToCart = () => {
    if (totalItems === 0) return;
    const items = Object.entries(selection)
      .map(([id, q]) => {
        const p = products.find((x) => x.id === id);
        return p ? { name: p.name, qty: q } : null;
      })
      .filter(Boolean);
    const summary = items.map((i) => `${i.qty}× ${i.name}`).join(" · ");
    const bundleProduct = {
      id: `bundle-${Date.now()}`,
      name: `Custom Bundle · ${totalItems} ${totalItems === 1 ? "box" : "boxes"}${tier.off ? ` (-${tier.off}%)` : ""}`,
      slug: "bundle",
      price: total,
      image: "/products/combo-essentials.jpg",
      bundle_summary: summary,
    };
    add(bundleProduct, 1);
    toast.success("Bundle reserved. Reviewing in cart.");
    reset();
    setIsOpen(true);
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="bundle-builder-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <div className="label-eyebrow mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Curate Your Own
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95]">
            Build the <span className="italic text-gradient-gold">Bundle</span>.
          </h1>
          <p className="text-mist font-light leading-relaxed text-lg mt-6 max-w-2xl">
            Compose a personalized box from across our five signatures.
            The more you compose, the more refined the price.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Selection grid */}
          <div className="lg:col-span-8">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-10">
              {signatures.map((p, i) => {
                const qty = selection[p.id] || 0;
                const active = qty > 0;
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className={`relative group ${active ? "" : ""}`}
                    data-testid={`bundle-card-${p.id}`}
                  >
                    <div className={`product-image-wrap aspect-[4/5] border transition-colors ${active ? "border-gold" : "hairline"}`}>
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute top-4 left-4 label-eyebrow bg-ink-950/70 px-3 py-1.5 border border-white/10">
                        {p.category_name}
                      </div>
                      {active && (
                        <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gold text-ink-950 flex items-center justify-center">
                          <Check className="w-4 h-4 stroke-[2]" />
                        </div>
                      )}
                    </div>

                    <div className="mt-5 flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-serif text-2xl text-bone leading-tight">{p.name}</h3>
                        <p className="text-mist text-xs italic mt-1 truncate">{p.tagline}</p>
                        <p className="text-gold text-sm mt-2">₹{p.price} <span className="text-mist text-xs">/ box</span></p>
                      </div>

                      {qty === 0 ? (
                        <button
                          onClick={() => update(p.id, 1)}
                          className="btn-ghost shrink-0 !px-5 !py-3"
                          data-testid={`bundle-add-${p.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                      ) : (
                        <div className="flex items-center border border-gold shrink-0">
                          <button
                            onClick={() => update(p.id, -1)}
                            className="w-10 h-10 text-bone hover:text-gold"
                            data-testid={`bundle-dec-${p.id}`}
                          >
                            <Minus className="w-3.5 h-3.5 mx-auto" />
                          </button>
                          <span className="w-8 text-center font-serif text-lg text-gold" data-testid={`bundle-qty-${p.id}`}>
                            {qty}
                          </span>
                          <button
                            onClick={() => update(p.id, 1)}
                            className="w-10 h-10 text-bone hover:text-gold"
                            data-testid={`bundle-inc-${p.id}`}
                          >
                            <Plus className="w-3.5 h-3.5 mx-auto" />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Sticky summary */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 bg-ink-900 border border-white/10 p-8 space-y-6" data-testid="bundle-summary">
              <div>
                <div className="label-eyebrow mb-2">Your Composition</div>
                <h2 className="font-serif text-3xl text-bone leading-tight">
                  {totalItems === 0 ? "Begin curating" : `${totalItems} ${totalItems === 1 ? "box" : "boxes"}`}
                </h2>
              </div>

              {/* Tier indicator */}
              <div className="space-y-3">
                {TIERS.slice(1).map((t) => {
                  const reached = totalItems >= t.min;
                  return (
                    <div
                      key={t.min}
                      className={`flex items-center justify-between text-xs tracking-[0.18em] uppercase border px-4 py-3 ${
                        reached ? "border-gold text-gold" : "border-white/10 text-mist"
                      }`}
                      data-testid={`bundle-tier-${t.min}`}
                    >
                      <span className="flex items-center gap-2">
                        {reached ? <Check className="w-3 h-3" /> : <span>{t.min}+</span>}
                        {t.label}
                      </span>
                      <span>{t.off}%</span>
                    </div>
                  );
                })}
                {nextTier && totalItems > 0 && (
                  <p className="text-mist text-xs font-light italic">
                    Add {nextTier.min - totalItems} more to unlock <span className="text-gold">-{nextTier.off}%</span>.
                  </p>
                )}
              </div>

              {/* Selected items */}
              <AnimatePresence>
                {totalItems > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-white/10 pt-5 space-y-3 overflow-hidden"
                  >
                    {Object.entries(selection).map(([id, q]) => {
                      const p = products.find((x) => x.id === id);
                      if (!p) return null;
                      return (
                        <div key={id} className="flex items-center justify-between text-sm" data-testid={`bundle-line-${id}`}>
                          <span className="text-bone font-light truncate pr-2">
                            <span className="text-gold">{q}×</span> {p.name}
                          </span>
                          <button
                            onClick={() => setSelection((s) => { const c = { ...s }; delete c[id]; return c; })}
                            className="text-mist hover:text-burgundy"
                            aria-label="Remove"
                            data-testid={`bundle-remove-${id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Totals */}
              <div className="border-t border-white/10 pt-5 space-y-2 text-sm">
                <div className="flex justify-between text-mist font-light">
                  <span>Subtotal</span>
                  <span data-testid="bundle-subtotal">₹{subtotal}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="flex justify-between text-gold font-light">
                    <span>Discount ({tier.off}%)</span>
                    <span data-testid="bundle-discount">−₹{discountAmt}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-3 border-t border-white/5">
                  <span className="label-eyebrow text-mist">Total</span>
                  <span className="num-display text-3xl text-gold" data-testid="bundle-total">₹{total}</span>
                </div>
              </div>

              <button
                onClick={addBundleToCart}
                disabled={totalItems === 0}
                className="btn-primary w-full disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="bundle-add-to-cart-btn"
              >
                Add bundle to cart <ArrowRight className="w-3.5 h-3.5" />
              </button>
              {totalItems > 0 && (
                <button
                  onClick={reset}
                  className="w-full text-xs tracking-[0.2em] uppercase text-mist hover:text-burgundy"
                  data-testid="bundle-reset-btn"
                >
                  Start over
                </button>
              )}

              <Link to="/quiz" className="block text-center text-xs tracking-[0.2em] uppercase text-gold/80 hover:text-gold pt-2">
                Not sure? Take the quiz →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
